import React, { useState, useEffect, useRef } from 'react'
import { DollarSignIcon, FileTextIcon, ClockIcon, CheckCircleIcon, InfoIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { fetchContractMilestones, summarizeMilestones } from '../lib/milestoneBilling'

interface Contract {
  id: string
  contract_number: string
  title: string
  contract_type: string
  billing_frequency?: string
  contract_total_amount?: number
  total_amount?: number
}

interface BillingOptions {
  billing_type: 'direct' | 'contract' | 'time_materials' | 'included'
  billing_contract_id?: string
  billing_notes?: string
  hourly_rate?: number
  hours_required?: number
}

interface JobBillingOptionsProps {
  customerId?: string
  proposalId?: string
  initialOptions?: Partial<BillingOptions>
  onOptionsChange: (options: BillingOptions) => void
}

const BILLING_TYPE_INFO = {
  direct: {
    label: 'Direct Billing',
    description: 'This job will be invoiced directly to the customer',
    icon: DollarSignIcon,
    color: 'blue',
  },
  contract: {
    label: 'Part of Contract',
    description: 'This job is included in a contract and will be billed through contract invoicing',
    icon: FileTextIcon,
    color: 'green',
  },
  time_materials: {
    label: 'Time & Materials',
    description: 'Bill based on actual hours worked plus materials used',
    icon: ClockIcon,
    color: 'purple',
  },
  included: {
    label: 'No Charge / Included',
    description: 'This job will not be billed (warranty, goodwill, etc.)',
    icon: CheckCircleIcon,
    color: 'gray',
  },
}

export function JobBillingOptions({
  customerId,
  proposalId,
  initialOptions,
  onOptionsChange,
}: JobBillingOptionsProps) {
  const [options, setOptions] = useState<BillingOptions>({
    billing_type: 'direct',
    ...initialOptions,
  })
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loadingContracts, setLoadingContracts] = useState(false)
  const [progressSummary, setProgressSummary] = useState<{
    billed: number
    total: number
    remaining: number
    nextLabel?: string
    nextAmount?: number
  } | null>(null)
  const emitUserChange = useRef(false)

  useEffect(() => {
    if (customerId) {
      fetchCustomerContracts()
    }
  }, [customerId])

  // Sync from parent when async prefill (e.g. contract load) updates initialOptions
  useEffect(() => {
    if (!initialOptions) return
    setOptions((prev) => ({
      ...prev,
      ...initialOptions,
    }))
  }, [
    initialOptions?.billing_type,
    initialOptions?.billing_contract_id,
    initialOptions?.billing_notes,
    initialOptions?.hourly_rate,
    initialOptions?.hours_required,
  ])

  useEffect(() => {
    // Only push to parent when the user changed something — avoid overwriting
    // async contract billing prefill with the default `direct` seed.
    if (!emitUserChange.current) return
    onOptionsChange(options)
  }, [options, onOptionsChange])

  useEffect(() => {
    const loadProgressSummary = async () => {
      if (options.billing_type !== 'contract' || !options.billing_contract_id) {
        setProgressSummary(null)
        return
      }
      const contract = contracts.find((c) => c.id === options.billing_contract_id)
      if (!contract || contract.billing_frequency !== 'progress') {
        setProgressSummary(null)
        return
      }
      try {
        const milestones = await fetchContractMilestones(contract.id)
        const contractTotal =
          Number(contract.contract_total_amount) || Number(contract.total_amount) || 0
        const summary = summarizeMilestones(milestones, contractTotal)
        setProgressSummary({
          billed: summary.billed,
          total: summary.total,
          remaining: summary.remaining,
          nextLabel: summary.nextPending?.label,
          nextAmount: summary.nextPending?.amount,
        })
      } catch {
        setProgressSummary(null)
      }
    }
    loadProgressSummary()
  }, [options.billing_type, options.billing_contract_id, contracts])

  const fetchCustomerContracts = async () => {
    if (!customerId) return

    setLoadingContracts(true)
    try {
      const { data, error } = await supabase
        .from('contracts')
        .select('id, contract_number, title, contract_type, billing_frequency, contract_total_amount, total_amount')
        .eq('customer_id', customerId)
        .in('status', ['active', 'pending'])
        .order('created_at', { ascending: false })

      if (error) throw error
      setContracts(data || [])

      if (proposalId && data && data.length > 0) {
        const { data: contractFromProposal } = await supabase
          .from('contracts')
          .select('id')
          .eq('proposal_id', proposalId)
          .single()

        if (contractFromProposal) {
          emitUserChange.current = true
          setOptions((prev) => ({
            ...prev,
            billing_type: 'contract',
            billing_contract_id: contractFromProposal.id,
          }))
        }
      }
    } catch (err) {
      console.error('Error fetching contracts:', err)
    } finally {
      setLoadingContracts(false)
    }
  }

  const updateOptions = (updates: Partial<BillingOptions>) => {
    emitUserChange.current = true
    setOptions((prev) => ({ ...prev, ...updates }))
  }

  const getBillingTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      blue: 'border-blue-500 bg-blue-50',
      green: 'border-green-500 bg-green-50',
      purple: 'border-purple-500 bg-purple-50',
      gray: 'border-gray-400 bg-gray-50',
    }
    const info = BILLING_TYPE_INFO[type as keyof typeof BILLING_TYPE_INFO]
    return colors[info?.color || 'gray']
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Billing Method
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(Object.keys(BILLING_TYPE_INFO) as Array<keyof typeof BILLING_TYPE_INFO>).map((type) => {
            const info = BILLING_TYPE_INFO[type]
            const Icon = info.icon
            const isSelected = options.billing_type === type

            return (
              <button
                key={type}
                type="button"
                onClick={() => updateOptions({ billing_type: type })}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? getBillingTypeColor(type)
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon className={`w-5 h-5 mt-0.5 ${isSelected ? 'text-gray-700' : 'text-gray-400'}`} />
                  <div>
                    <p className={`font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                      {info.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{info.description}</p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {options.billing_type === 'contract' && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Contract
          </label>
          {loadingContracts ? (
            <p className="text-sm text-gray-500">Loading contracts...</p>
          ) : contracts.length === 0 ? (
            <div className="text-sm text-amber-700 bg-amber-50 p-3 rounded">
              <InfoIcon className="w-4 h-4 inline mr-2" />
              No active contracts found for this customer.
            </div>
          ) : (
            <select
              value={options.billing_contract_id || ''}
              onChange={(e) => updateOptions({ billing_contract_id: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select a contract...</option>
              {contracts.map((contract) => (
                <option key={contract.id} value={contract.id}>
                  {contract.contract_number} - {contract.title} ({contract.contract_type})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {progressSummary && (
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-900">
          <p className="font-medium mb-1">Progress billing on this contract</p>
          <p>
            Billed: ${progressSummary.billed.toFixed(2)} of ${progressSummary.total.toFixed(2)}
            (remaining ${progressSummary.remaining.toFixed(2)})
          </p>
          {progressSummary.nextLabel && (
            <p className="mt-1 text-indigo-800">
              Next pending milestone: {progressSummary.nextLabel} (
              ${Number(progressSummary.nextAmount || 0).toFixed(2)})
            </p>
          )}
        </div>
      )}

      {options.billing_type === 'time_materials' && (
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hourly Rate ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={options.hourly_rate || ''}
                onChange={(e) => updateOptions({ hourly_rate: parseFloat(e.target.value) || undefined })}
                placeholder="e.g., 75.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Hours
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={options.hours_required || ''}
                onChange={(e) => updateOptions({ hours_required: parseFloat(e.target.value) || undefined })}
                placeholder="e.g., 4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          {options.hourly_rate && options.hours_required && (
            <div className="mt-3 p-3 bg-white rounded border border-purple-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Estimated Labor Cost:</span>
                <span className="text-lg font-semibold text-purple-700">
                  ${(options.hourly_rate * options.hours_required).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {options.billing_type === 'included' && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for No Charge
          </label>
          <select
            value={options.billing_notes || ''}
            onChange={(e) => updateOptions({ billing_notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select reason...</option>
            <option value="warranty">Warranty Service</option>
            <option value="goodwill">Customer Goodwill</option>
            <option value="callback">Callback / Redo</option>
            <option value="internal">Internal / Training</option>
            <option value="other">Other</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Billing Notes (Optional)
        </label>
        <textarea
          value={options.billing_notes || ''}
          onChange={(e) => updateOptions({ billing_notes: e.target.value })}
          rows={2}
          placeholder="Any special billing instructions or notes..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>
    </div>
  )
}

export default JobBillingOptions
